/**
 * ReporterHome — Sections grid.
 * Each section is a readiness tile. Click → open the section workspace.
 *
 * Visual cadence: clean cards, light backgrounds, cardinal red on the leading
 * section ID. No icons that don't carry meaning.
 */

import React, { useState } from 'react';
import PendingUniverseRequestBanner from '../../components/PendingUniverseRequestBanner';
import MappingDrawer from './components/MappingDrawer';

// 19 tiles — ONE PER HPMS FILE the plan submits, matching the official
// CY2026 File Record Layouts. The consolidation (Payments was 7 tiles → 1,
// OD/Recon was 4 tiles → 1) reflects how HPMS actually receives the files:
// one tab-delimited .TXT per measure, categories live as columns inside.
//
// 'mode' decides what each tile can do:
//   'full'             — File-shape check + Records recount (engine exists today)
//   'file_shape_only'  — File-shape check only (kept for tiles whose engine regresses)
//   'preflight'        — File-shape check only by design (no published Layout to recount against)
//
// 'measure_id' routes to the /api/reporter/file-shape/:measure_id endpoint.
// 'id' is the section identifier ReporterSectionWork dispatches on: canonical
// tiles (II, II.CDR, MTM, MPPP, I.D, V.1.D, V.2.D, II.5.D) route to the
// canonical engine stack via CANONICAL_DISPATCH; every other tile keeps the
// legacy /validate/section/:id path via SECTION_DISPATCH.
const SECTION_CATALOG = [
  // ────── Part C ──────
  { id: 'I',     measure_id: 'grievances-c',         group: 'Part C', label: 'Grievances',                            mode: 'full' },
  { id: 'II',    measure_id: 'odr',                  group: 'Part C', label: 'Organization Determinations & Reconsiderations', mode: 'full', note: 'Consolidated from old II.1–II.4. File check and recount run against the unified ODR layout.' },
  { id: 'II.5',  measure_id: 'reopenings-c',         group: 'Part C', label: 'Reopenings',                            mode: 'full' },
  { id: 'III',   measure_id: 'egps',                 group: 'Part C', label: 'Employer Group Plan Sponsors',          mode: 'full' },
  { id: 'IV',    measure_id: 'snp-hra',              group: 'Part C', label: 'SNP Care Management (HRA)',             mode: 'full' },
  { id: 'V.1',   measure_id: 'enrollment-c',         group: 'Part C', label: 'Enrollment',                            mode: 'full' },
  { id: 'V.2',   measure_id: 'disenrollment-c',      group: 'Part C', label: 'Disenrollment',                         mode: 'full' },
  { id: 'VI',    measure_id: 'rewards-incentives',   group: 'Part C', label: 'Rewards & Incentives',                  mode: 'full' },
  { id: 'VII',   measure_id: 'payments',             group: 'Part C', label: 'Payments to Providers',                 mode: 'full', note: 'Consolidated from old VII.1–VII.7. Recount runs against the unified Payments layout.' },
  { id: 'VIII',  measure_id: 'supplemental-benefit', group: 'Part C', label: 'Supplemental Benefit Utilization',      mode: 'full', badge: 'CY2026 expanded' },
  { id: 'IX',    measure_id: 'dsnp-eac',             group: 'Part C', label: 'D-SNP Enrollee Advisory Committee',     mode: 'preflight', note: 'CMS Layout PDF not yet published; file-shape inferred from Reporting Requirements.' },
  { id: 'X',     measure_id: 'dsnp-admit',           group: 'Part C', label: 'D-SNP Admission Notifications',         mode: 'full' },

  // ────── Part D (new for CY2026 in this tool) ──────
  { id: 'I.D',   measure_id: 'grievances-d',         group: 'Part D', label: 'Grievances',                            mode: 'full', badge: 'new' },
  { id: 'V.1.D', measure_id: 'enrollment-d',         group: 'Part D', label: 'Enrollment',                            mode: 'full', badge: 'new' },
  { id: 'V.2.D', measure_id: 'disenrollment-d',      group: 'Part D', label: 'Disenrollment',                         mode: 'full', badge: 'new' },
  { id: 'II.5.D',measure_id: 'reopenings-d',         group: 'Part D', label: 'Reopenings (within CDR)',               mode: 'full', badge: 'new' },
  { id: 'II.CDR',measure_id: 'cdr',                  group: 'Part D', label: 'Coverage Determinations & Redeterminations', mode: 'full', badge: 'new' },
  { id: 'MTM',   measure_id: 'mtm',                  group: 'Part D', label: 'Medication Therapy Management',         mode: 'full', badge: 'PHI' },
  { id: 'MPPP',  measure_id: 'm3p',                  group: 'Part D', label: 'Medicare Prescription Payment Plan',    mode: 'full', badge: 'CY2026 new' },
];

const STATUS_STYLES = {
  full:             { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'File check + Records recount' },
  file_shape_only:  { badge: 'bg-amber-50 text-amber-700 border-amber-200',       label: 'File check only (recount engine pending)' },
  preflight:        { badge: 'bg-sky-50 text-sky-700 border-sky-200',             label: 'File check only' },
};

export default function ReporterHome({ sections, onOpen }) {
  const [mappingFor, setMappingFor] = useState(null); // { measureId, label }
  return (
    <div>
      <PendingUniverseRequestBanner consumerAppFilter="reporter" />
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">CMS Part C/D Reporting Requirements</div>
        <h2 className="text-lg font-semibold text-slate-900">Check it before you submit it.</h2>
        <p className="text-sm text-slate-600 max-w-2xl mt-1">
          Pick a section. Drop in the file you're about to send to HPMS and we'll tell you whether HPMS will
          accept it. Drop in the records behind the file too and we'll recount them, compare to your file,
          and show you every miscount — with the rule behind each finding — before you upload.
        </p>
      </div>

      {['Part C', 'Part D'].map(group => {
        const tiles = SECTION_CATALOG.filter(s => s.group === group);
        return (
          <div key={group} className="mb-6">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{group}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {tiles.map(s => {
                const style = STATUS_STYLES[s.mode];
                return (
                  <button
                    key={s.id}
                    onClick={() => onOpen(s.id, s.mode)}
                    className="text-left border bg-white p-4 transition-shadow border-slate-200 hover:border-red-700 hover:shadow-sm cursor-pointer"
                    title={s.note || ''}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="font-mono text-xs text-red-700 tracking-wider">§ {s.id}</div>
                      <span className={`text-[10px] font-mono uppercase tracking-wider border px-1.5 py-0.5 ${style.badge}`}>
                        {style.label}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <span>{s.label}</span>
                      {s.badge && (
                        <span className="text-[9px] font-mono uppercase tracking-wider border border-slate-200 text-slate-500 px-1.5 py-0.5 rounded">
                          {s.badge}
                        </span>
                      )}
                    </div>
                    {s.note && (
                      <div className="text-[11px] text-slate-500 mt-2 leading-snug">{s.note}</div>
                    )}
                    {s.measure_id && (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setMappingFor({ measureId: s.measure_id, label: s.label }); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setMappingFor({ measureId: s.measure_id, label: s.label }); } }}
                        className="text-[11px] mt-3 text-red-700 hover:text-red-900 font-medium inline-flex items-center gap-1 cursor-pointer"
                      >
                        Map your columns →
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      {mappingFor && (
        <MappingDrawer
          measureId={mappingFor.measureId}
          measureLabel={mappingFor.label}
          onClose={() => setMappingFor(null)}
        />
      )}
    </div>
  );
}

export { SECTION_CATALOG };
